import { DT } from "../../constants.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, MoveKind, hv, setHv } from "../../sim/components.ts";
import { aim, applyDamage, applyStatus, hitscan, isEnemy, lockOn, playersInRadius } from "../../sim/combat.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, flight, ready, secs, spreadDir, startMove, trigger, useUlt, wishDir } from "../kitlib.ts";

export const V = { fuel: 0, barrage: 1, lockTarget: 2, legend: 3 } as const;
export const FX = { Blaster: fxk(HeroId.StarLorde, 0), Barrage: fxk(HeroId.StarLorde, 1), Lock: fxk(HeroId.StarLorde, 2) } as const;
const FUEL_MAX = 1800;

const def: HeroDef = {
  id: HeroId.StarLorde,
  slug: "star-lorde",
  name: "Star-Lorde",
  role: Role.Damage,
  color: "#d9722b",
  blurb: "Dual-blaster skirmisher with fuel flight, evasive reload dashes, spin barrage and lock-on ultimate.",
  health: 150,
  armor: 0,
  shields: 125,
  radius: 0.4,
  height: 1.85,
  headRadius: 0.23,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 50,
  reloadTime: 1.5,
  ultCost: 3100,
  abilities: {
    [Slot.Primary]: { name: "Mixtape Pistols", key: "LMB", description: "Fire dual rapid blasters with spread and critical hits.", cooldown: 0 },
    [Slot.Secondary]: { name: "Encore Dodge", key: "RMB", description: "Dash in your movement direction, become briefly invulnerable and reload.", cooldown: 5, charges: 2 },
    [Slot.Ability1]: { name: "Bootleg Boost", key: "Shift (hold)", description: "Spend fuel to fly forward at double speed.", cooldown: 0 },
    [Slot.Ability2]: { name: "Turntable Tantrum", key: "E", description: "Spin-fire at every enemy in 8m for 3 seconds.", cooldown: 8 },
    [Slot.Ultimate]: { name: "Headline Act", key: "Q", description: "Free-flight with rapid lock-on blaster fire for 8 seconds.", cooldown: 0 },
  },
  passive: { name: "Regenerative Swagger", description: "A shield pool sits on top of Star-Lorde's low health." },
};

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.fuel, FUEL_MAX);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      setHv(c, eid, V.lockTarget, 0);
      if (hv(c, eid, V.barrage) > 0) {
        setHv(c, eid, V.barrage, hv(c, eid, V.barrage) - 1);
        action(sim, eid, ActionKind.AltFire, 2);
        if (sim.tick % 6 === 0) {
          for (const { eid: t, d } of playersInRadius(sim, sim.posOf(eid), 8, (e) => isEnemy(sim, eid, e), false)) {
            const k = d <= 4 ? 1 : 1 - ((d - 4) / 4) * 0.2;
            applyDamage(sim, t, 8 * k, eid);
          }
        }
      }
      if (hv(c, eid, V.legend) > 0) setHv(c, eid, V.legend, hv(c, eid, V.legend) - 1);
      if (!(input.buttons & Btn.Ability1) && hv(c, eid, V.legend) <= 0) setHv(c, eid, V.fuel, Math.min(FUEL_MAX, hv(c, eid, V.fuel) + 120 * DT));

      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(8);
        setHv(c, eid, V.legend, secs(8));
        action(sim, eid, ActionKind.Ult, 30);
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        c.Weapon.ammo[eid] = def.ammo;
        applyStatus(sim, eid, "invuln", 0.37, { source: eid });
        const d = wishDir(input);
        startMove(sim, eid, MoveKind.Dash, secs(0.22), 18, { x: d.x * 18, y: 0, z: d.z * 18 });
        action(sim, eid, ActionKind.Cast, 12);
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        setHv(c, eid, V.barrage, secs(3));
        action(sim, eid, ActionKind.AltFire, 20);
      }

      const legend = hv(c, eid, V.legend) > 0;
      if (legend && sim.tick % 3 !== 0) {
        const target = lockOn(sim, eid, 40, 0.7, false);
        if (target !== undefined) {
          setHv(c, eid, V.lockTarget, c.Net.id[target]!);
          applyDamage(sim, target, 6.5, eid);
          const a = aimAt(sim, eid, target);
          hitscan(sim, eid, a.origin, a.dir, { range: 40, damage: 0, fxKind: FX.Lock, tracer: true });
          action(sim, eid, ActionKind.Fire, 2);
        }
      }

      if (!legend && trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        c.Weapon.fireCd[eid] = 2;
        action(sim, eid, ActionKind.Fire, 2);
        const a = aimAt(sim, eid);
        const spread = c.Weapon.shots[eid]! > 5 ? 0.024 : c.Weapon.shots[eid]! > 2 ? 0.015 : 0.004;
        hitscan(sim, eid, a.origin, spreadDir(a.dir, spread, c.Weapon.shots[eid]! * 71), { range: 45, damage: 6.5, falloffStart: 10, falloffEnd: 20, falloffMin: 0.6, canCrit: true, fxKind: FX.Blaster, tracer: true });
      }
    },
    movement(sim, eid, input, mods) {
      if (hv(sim.c, eid, V.legend) > 0) {
        flight(sim, eid, input, mods, { speed: sim.hero(eid).speed * 1.2, accel: 40, vertical: 7, pitchClimb: true });
      } else if ((input.buttons & Btn.Ability1) && hv(sim.c, eid, V.fuel) > 0) {
        flight(sim, eid, input, mods, { speed: sim.hero(eid).speed * 2, accel: 42, vertical: 7, pitchClimb: true, fuelIdx: V.fuel, drain: 300 });
      }
    },
  },
  fx: {
    [FX.Blaster]: { color: "#f6d36a" },
    [FX.Barrage]: { color: "#ff8a45" },
    [FX.Lock]: { color: "#fff06a" },
  },
};

function aimAt(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, target?: number) {
  if (target === undefined) return sim ? requireAim(sim, eid) : undefined!;
  const a = requireAim(sim, eid);
  const pos = sim.posOf(target);
  const center = { x: pos.x, y: pos.y + sim.hero(target).height * 0.55, z: pos.z };
  const dx = center.x - a.origin.x;
  const dy = center.y - a.origin.y;
  const dz = center.z - a.origin.z;
  const l = Math.hypot(dx, dy, dz) || 1;
  return { origin: a.origin, dir: { x: dx / l, y: dy / l, z: dz / l }, point: center };
}

function requireAim(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  return aim(sim, eid, 80);
}

export default module;
